/**
 * apps/command/src/components/RemoteActions.tsx — §4.5 on the kiosk detail
 * screen.
 *
 * ============================================================================
 * THE PANEL SHOWS «Fuqaro murojaat qoldirmoqda — kutilmoqda» AND NO OVERRIDE.
 * ============================================================================
 *
 * §3.1 as amended by D-MBT-10: a disruptive command issued while a citizen is
 * recording is DEFERRED by the kiosk and runs when the screen returns to idle.
 * The operator's job at that moment is to do nothing, and this screen is built
 * to make that obvious — the status is a plain sentence about a citizen, not an
 * error, and there is no button beside it.
 *
 * There is deliberately no "force" control, because there is no API that would
 * accept one: §3.1 gives the server no authority over the lock, so the panel
 * cannot offer what the server cannot do.
 *
 * ============================================================================
 * THE TYPED CONFIRMATION IS THE KIOSK'S OWN CODE.
 * ============================================================================
 *
 * §4.5 requires one for the dangerous set, and the failure it defends against
 * is not misunderstanding — it is muscle memory on the wrong row of a 35-tile
 * grid. A second "are you sure" button defends against neither; typing `T2K`
 * when you meant `K7T` is a thing you notice.
 *
 * The code is shown in the hint, which sounds like it defeats the purpose and
 * does not: the operator is not being tested on recall, they are being made to
 * look at WHICH kiosk they are about to reboot.
 */
import { useCallback, useEffect, useState } from 'react';
import { isDangerousCommand, type KioskCommand } from '@navoiy/domain';
import type { components } from '@navoiy/api-types';
import { api, ApiError, unwrap } from '../api/client.js';
import { useSession } from '../auth/SessionContext.js';
import { hasKey, t } from '../i18n/index.js';
import { ageLabel } from '../lib/format.js';
import { Button, Field, Notice, Panel, PanelHeader, Skeleton, TextInput } from '../ui/index.jsx';

type MbtCommand = components['schemas']['MbtCommand'];

/**
 * The verbs this panel offers today.
 *
 * A SUBSET of §4.5's seventeen, and the subset the kiosk actually implements
 * (v0.5.8). Offering a button whose only possible outcome is `unsupported`
 * would be offering an operator a control that cannot work — §15.7's rule
 * against exactly that, applied to a command list.
 */
const OFFERED: readonly KioskCommand[] = [
  'app.restart',
  'content.refresh',
  'diagnostics.run',
  /**
   * Added once the kiosk implemented it (v0.6.7). Without this, getting a
   * release onto a machine meant carrying a signed installer to it: the
   * automatic path correctly refuses to restart a kiosk in front of citizens
   * outside 01:00–06:00, so an operator who wants it NOW had no way to say so.
   * The appeal lock still defers it while a citizen is recording.
   */
  'update.install',
  'machine.reboot',
];

function typeLabel(type: string): string {
  return hasKey(`command.type.${type}`) ? t(`command.type.${type}`) : type;
}

function statusLabel(status: string): string {
  return hasKey(`command.status.${status}`) ? t(`command.status.${status}`) : status;
}

export function RemoteActions({
  deviceId,
  shortCode,
  onExpired,
}: {
  deviceId: string;
  shortCode: string;
  onExpired: () => void;
}): React.JSX.Element | null {
  const { allows } = useSession();
  const [history, setHistory] = useState<MbtCommand[] | null>(null);
  const [selected, setSelected] = useState<KioskCommand | null>(null);
  const [reason, setReason] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1_000);
    return () => clearInterval(timer);
  }, []);

  const load = useCallback(async () => {
    try {
      const data = unwrap(
        await api().GET('/mbt/fleet/kiosks/{device_id}/commands', {
          params: { path: { device_id: deviceId } },
        }),
      );
      setHistory(data.items as MbtCommand[]);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) onExpired();
      // A failed history read leaves the previous list in place. During an
      // incident the last known list is worth more than a blank panel.
    }
  }, [deviceId, onExpired]);

  useEffect(() => {
    void load();
    // Polled, because a command's status moves without anybody clicking:
    // queued → delivered → deferred → acked. §15.9's "live without refresh"
    // applies to a command's progress as much as to a tile's.
    const timer = setInterval(() => void load(), 5_000);
    return () => clearInterval(timer);
  }, [load]);

  // §4.5 / D-MBT-04. A technician sees the HISTORY (an incident review that
  // hides the actions taken is not a review) and no controls.
  const mayIssue = allows('mbt.command.issue');

  async function issue(): Promise<void> {
    if (!selected) return;
    const dangerous = isDangerousCommand(selected);
    if (dangerous && !reason.trim()) {
      setError(t('command.reasonRequired'));
      return;
    }
    if (dangerous && confirm !== shortCode) {
      setError(t('command.confirmMismatch'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api().POST('/mbt/fleet/kiosks/{device_id}/commands', {
        params: { path: { device_id: deviceId } },
        body: {
          type: selected,
          ...(reason.trim() ? { reason: reason.trim() } : {}),
          ...(dangerous ? { confirm } : {}),
        },
      });
      setSelected(null);
      setReason('');
      setConfirm('');
      await load();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) onExpired();
      else if (caught instanceof ApiError && caught.status === 403) {
        setError(t('command.noPermission'));
      } else setError(t('command.failed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel>
      <PanelHeader title={t('command.title')} />
      <div className="space-y-4 px-4 py-4">
        {mayIssue ? (
          <>
            <div className="flex flex-wrap gap-2">
              {OFFERED.map((type) => (
                <Button
                  key={type}
                  tone={selected === type ? 'primary' : 'quiet'}
                  onClick={() => {
                    setSelected(selected === type ? null : type);
                    setError(null);
                  }}
                >
                  {typeLabel(type)}
                </Button>
              ))}
            </div>

            {selected ? (
              <div className="space-y-3 rounded border border-hairline p-4">
                {isDangerousCommand(selected) ? (
                  <>
                    {/* Not "are you sure" — WHY this one is different. The test
                        for dangerous is "can the kiosk come back by itself". */}
                    <Notice tone="warn">{t('command.dangerous')}</Notice>
                    <Field label={t('command.reason')} hint={t('command.reasonHint')}>
                      <TextInput
                        value={reason}
                        onChange={(event) => setReason(event.target.value)}
                        maxLength={500}
                      />
                    </Field>
                    <Field
                      label={t('command.confirm')}
                      hint={t('command.confirmHint', { code: shortCode })}
                    >
                      <TextInput
                        value={confirm}
                        onChange={(event) => setConfirm(event.target.value)}
                      />
                    </Field>
                  </>
                ) : (
                  <Field label={t('command.reason')} hint={t('command.reasonHint')}>
                    <TextInput
                      value={reason}
                      onChange={(event) => setReason(event.target.value)}
                      maxLength={500}
                    />
                  </Field>
                )}

                {error ? <Notice tone="crit">{error}</Notice> : null}

                <div className="flex gap-3">
                  <Button onClick={() => void issue()} disabled={busy}>
                    {t('command.issue')}
                  </Button>
                  <Button
                    tone="quiet"
                    onClick={() => {
                      setSelected(null);
                      setError(null);
                    }}
                  >
                    {t('command.cancel')}
                  </Button>
                </div>
              </div>
            ) : null}
          </>
        ) : null}

        <div>
          <p className="mb-2 text-label text-ink-500">{t('command.history')}</p>
          {history === null ? (
            <Skeleton className="h-16" />
          ) : history.length === 0 ? (
            <p className="py-2 text-micro text-ink-500">{t('command.historyEmpty')}</p>
          ) : (
            <ul className="divide-y divide-hairline">
              {history.map((command) => (
                <li key={command.id} className="py-2">
                  <p className="flex items-baseline justify-between gap-3 text-label">
                    <span className="text-ink-100">{typeLabel(command.type)}</span>
                    <span
                      className={`shrink-0 text-micro ${
                        command.status === 'failed' || command.status === 'expired'
                          ? 'text-crit'
                          : command.status === 'deferred'
                            ? 'text-warn'
                            : command.status === 'acked'
                              ? 'text-ok'
                              : 'text-ink-500'
                      }`}
                    >
                      {statusLabel(command.status)}
                    </span>
                  </p>
                  <p className="text-micro tabular-nums text-ink-500">
                    {ageLabel(new Date(command.issued_at), now, t)} · {command.issued_by_name}
                    {command.reason ? ` · ${command.reason}` : ''}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Panel>
  );
}
